/**
 * Reading an FBI acknowledgment the way an operator needs to read it.
 *
 * The acknowledgment is four possible events merged onto one record — the upload ARV, the submission ARV,
 * the FBI's response, and a router-side submission error — and none of them are self-explanatory. This turns
 * that into labelled rows plus one honest verdict.
 *
 * The trap this exists to contain: the response field named `error` holds the word **"Successful"** on a
 * transaction that worked. The router maps the FBI's `Error_short_text` straight onto it, and the FBI writes
 * its verdict there whether or not the verdict is bad. Reading "error is present" as "it failed" once marked
 * a passing DOCE background check as ERROR — the customer's search came back clean and our console said it
 * had failed. So the words are classified here, in one place, rather than at each render site.
 */

export interface TransactionAck {
  upload?: string;
  submission?: string;
  response?: {
    dts?: string;
    tot?: string;
    tcn?: string;
    tcr?: string;
    srf?: string;
    error?: string;
    error_desc?: string;
  };
  submissionError?: {
    reason?: string;
    detail?: string;
    occurredOn?: string;
    submitterId?: string;
  };
}

/**
 * Words the FBI puts in `error` that mean nothing is wrong.
 *
 * Kept identical to `SUCCESS_TEXT` in `services/handlers/fbiAck` on purpose: if the two ever disagree, the
 * console and the state machine will disagree about the same transaction, which is worse than either being
 * wrong on its own.
 */
const SUCCESS_TEXT = /^(successful|success|no\s*error|none|ok)$/i;

/** Whether an `error` value is actually a complaint. Blank is not, and neither is "Successful". */
export function isErrorText(value: string | undefined): boolean {
  const v = (value ?? '').trim();
  if (!v) return false;
  return !SUCCESS_TEXT.test(v);
}

export type AckVerdict = 'accepted' | 'rejected' | 'not-submitted' | 'awaiting' | 'none';

/**
 * What the acknowledgment amounts to.
 *
 * `not-submitted` is deliberately separate from `rejected`: a router-side `submissionError` means the FBI
 * never saw the file, which is a different problem with a different remedy from the FBI refusing one it did
 * receive. `awaiting` means we have an upload receipt but no verdict yet — which is not the same as `none`,
 * where nothing has come back at all and we cannot say whether it arrived.
 */
export function ackVerdict(ack: TransactionAck | null | undefined): AckVerdict {
  if (!ack) return 'none';
  if (ack.submissionError?.reason) return 'not-submitted';
  const r = ack.response;
  if (r && (r.error || r.tcr || r.dts)) return isErrorText(r.error) ? 'rejected' : 'accepted';
  if (ack.upload || ack.submission) return 'awaiting';
  return 'none';
}

export const VERDICT_LABEL: Record<AckVerdict, string> = {
  accepted: 'Accepted by the FBI',
  rejected: 'Rejected by the FBI',
  'not-submitted': 'Never reached the FBI',
  awaiting: 'Awaiting the FBI response',
  none: 'No acknowledgment received',
};

/**
 * Maps onto `.status-dot` tones, so an ACK reads like the rest of the console.
 *
 * Only `ok`, `warn`, `muted` and `bad` exist. `bad` is added alongside this for the two failure verdicts —
 * an earlier pass used `pend` for `awaiting` and it silently fell through to the default grey, which made a
 * REJECTED submission look no different from an ordinary one. `awaiting` uses `warn`, which is what amber
 * already means everywhere else here: nothing is wrong yet, but it is not finished.
 */
export const VERDICT_TONE: Record<AckVerdict, string> = {
  accepted: 'ok',
  rejected: 'bad',
  'not-submitted': 'bad',
  awaiting: 'warn',
  none: 'muted',
};

/** The tones `.status-dot` actually defines. A verdict mapped outside this set renders as unstyled grey. */
export const STATUS_DOT_TONES = ['ok', 'warn', 'bad', 'muted'] as const;

export interface AckRow {
  label: string;
  value: string;
  /** True for a value that is a genuine complaint, so the renderer can mark it without re-deciding. */
  bad?: boolean;
  /** Long free text (a router detail, an error description) — rendered on its own line rather than inline. */
  wide?: boolean;
}

/** The FBI's abbreviations spelled out. An operator should not need the EBTS open to read this panel. */
const FIELD_LABEL: Record<string, string> = {
  tot: 'Response type',
  tcn: 'TCN echoed back',
  tcr: 'Transaction control reference',
  srf: 'Search results found',
  dts: 'FBI processed at',
  error: 'FBI verdict',
  error_desc: 'FBI detail',
};

/**
 * The acknowledgment as rows, in the order it happened: what we sent, then what came back.
 *
 * Only what is present. An absent field means that event never arrived, and inventing an em dash for it
 * would make an incomplete acknowledgment look like a complete one with empty values.
 */
export function ackRows(ack: TransactionAck | null | undefined): AckRow[] {
  if (!ack) return [];
  const rows: AckRow[] = [];
  if (ack.upload) rows.push({ label: 'Upload receipt (ARV)', value: ack.upload });
  if (ack.submission) rows.push({ label: 'Submission receipt (ARV)', value: ack.submission });

  const r = ack.response ?? {};
  for (const key of ['dts', 'tot', 'tcr', 'srf', 'error', 'error_desc'] as const) {
    const value = r[key];
    if (!value) continue;
    rows.push({
      label: FIELD_LABEL[key] ?? key,
      value,
      ...(key === 'error' && isErrorText(value) ? { bad: true } : {}),
      ...(key === 'error_desc' ? { wide: true } : {}),
    });
  }

  const se = ack.submissionError;
  if (se?.reason) {
    rows.push({ label: 'Router refused it', value: se.reason, bad: true });
    if (se.detail) rows.push({ label: 'Detail', value: se.detail, bad: true, wide: true });
    if (se.submitterId) rows.push({ label: 'Submitter id', value: se.submitterId });
    if (se.occurredOn) rows.push({ label: 'Failed at', value: se.occurredOn });
  }
  return rows;
}

/**
 * `srf` is a one-letter flag and `f`/`t` is not something to show an operator.
 *
 * Returns null for anything unrecognised rather than guessing — a value we cannot explain is better shown
 * raw than mistranslated.
 */
export function searchResultsLabel(srf: string | undefined): string | null {
  const v = (srf ?? '').trim().toLowerCase();
  if (v === 'f') return 'No match found';
  if (v === 't') return 'Match found';
  return null;
}
